<template>
  <div class="cg-nav">
    <div class="cg-nav__inner content-column">
      <button class="cg-nav__brand cg-focus-ring" type="button" @click="goTo('goods')" aria-label="CardGenius — на главную">
        <span class="cg-nav__brand-mark" aria-hidden="true">
          <img class="cg-nav__brand-img cg-nav__brand-img--light" :src="brandLightDataUrl" alt="" />
          <img class="cg-nav__brand-img cg-nav__brand-img--dark" :src="brandDarkDataUrl" alt="" />
        </span>
      </button>

      <nav class="cg-nav__segments" aria-label="Основное меню">
        <div class="cg-nav__segments-plate">
          <button
            v-for="tab in segmentTabs"
            :key="tab.key"
            type="button"
            class="cg-nav__segment"
            :class="{ 'is-active': isActive(tab.key) }"
            @click="goTo(tab.key)"
          >
            <span class="cg-nav__segment-icon" aria-hidden="true">
              <Icon :name="tab.icon" :size="20" />
            </span>
            <span class="cg-nav__segment-label">{{ tab.label }}</span>
            <span v-if="tab.key === 'recommendations'" class="cg-nav__ai-badge" aria-label="AI">AI</span>
          </button>
        </div>
      </nav>

      <div class="cg-nav__actions">
        <Dropdown
          class="cg-nav__geni-dropdown"
          :text="`${geniBalance}`"
          value="geni"
          icon="lightning"
          icon-position="left"
          button-color="transparent"
          button-appearance="none"
          :button-hover="false"
          align="end"
          @click="onOpenBillingDropdown"
        >
          <div class="cg-nav__geni-menu">
            <div class="cg-nav__geni-current">
              <span>Текущий баланс</span>
              <GeniInline :amount="geniBalance" size="lg" theme="dark" />
            </div>
            <button
              v-for="pack in paidPacks"
              :key="pack.id"
              class="cg-nav__geni-pack"
              type="button"
              @click="openTopUpModal(pack.id)"
            >
              <span class="cg-nav__geni-pack-label">
                <span>{{ pack.label }}:</span>
                <GeniInline :amount="`+${pack.geni}`" size="xs" theme="dark" />
              </span>
              <span>{{ pack.rub }} ₸</span>
            </button>
          </div>
        </Dropdown>

        <button
          class="cg-nav__icon-action cg-focus-ring"
          :class="{ 'is-active': isActive('settings') }"
          type="button"
          aria-label="Настройки"
          @click="goTo('settings')"
        >
          <span class="cg-nav__icon-well" aria-hidden="true">
            <Icon name="user" :size="18" />
          </span>
        </button>
      </div>
    </div>
  </div>

  <BalanceModal
    :model-value="isBalanceModalOpen"
    :packs="packs"
    :current-balance="geniBalance"
    :is-paying="isPaying"
    :initial-pack-id="initialPackId"
    @close="closeTopUpModal"
    @pay="handleTopUp"
  />

  <!-- Mobile dock -->
  <div class="cg-dock" aria-hidden="false">
    <nav class="cg-dock__inner" aria-label="Основное меню (dock)">
      <button
        v-for="tab in dockTabs"
        :key="tab.key"
        type="button"
        class="cg-dock__item cg-focus-ring"
        :class="{ 'is-active': isActive(tab.key) }"
        :aria-label="tab.label"
        @click="goTo(tab.key)"
      >
        <Icon :name="tab.icon" :size="22" />
      </button>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
import { ROUTE_NAMES, type RouteName } from "@/router";
import { useRoute, useRouter } from "vue-router";

import brandFullDarkSvg from "@/assets/branding/Full/Dark.svg?raw";
import brandFullLightSvg from "@/assets/branding/Full/light.svg?raw";

import "./Navigation.scss";
import { storeToRefs } from "pinia";
import Icon from "../ui/Icon.vue";
import { computed, ref } from "vue";
import Dropdown from "@/components/ui/dropdown/Dropdown.vue";
import GeniInline from "@/components/billing/GeniInline.vue";
import BalanceModal from "@/components/billing/BalanceModal.vue";
import { useBilling } from "@/composables/useBilling";

/** Встроенный data: URL — работает при встраивании виджета, где отдельные файлы из ?url недоступны. */
function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const brandLightDataUrl = svgToDataUrl(brandFullLightSvg);
const brandDarkDataUrl = svgToDataUrl(brandFullDarkSvg);

type PrimaryNavTab = Extract<RouteName, "goods" | "recommendations">;
type DockNavTab = Extract<RouteName, "goods" | "recommendations" | "settings">;

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { geniBalance } = storeToRefs(authStore);
const { packs, ensurePacksLoaded, topUp, isPaying } = useBilling();

function isActive(name: RouteName): boolean {
  return route.name === name;
}

function goTo(name: RouteName): void {
  if (route.name === name) return;
  void router.push({ name });
}

const segmentTabs = ref([
  { key: "goods", label: "Контент", icon: "box" },
  { key: "recommendations", label: "Рекомендации", icon: "ai" },
] as { key: PrimaryNavTab; label: string; icon: string }[]);

const dockTabs = computed(
  () =>
    [
      ...segmentTabs.value,
      { key: "settings", label: "Настройки", icon: "user" },
    ] as { key: DockNavTab; label: string; icon: string }[],
);

const isBalanceModalOpen = ref(false);
const initialPackId = ref<string | null>(null);
const paidPacks = computed(() => packs.value.filter((pack) => pack.id !== "free"));

async function onOpenBillingDropdown() {
  await ensurePacksLoaded();
}

async function openTopUpModal(packId: string) {
  await ensurePacksLoaded();
  initialPackId.value = packId;
  isBalanceModalOpen.value = true;
}

function closeTopUpModal() {
  isBalanceModalOpen.value = false;
  initialPackId.value = null;
}

async function handleTopUp(pack: { id: string }) {
  await topUp(pack.id);
  closeTopUpModal();
}
</script>
